import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { 
  RefreshCw, 
  Power, 
  Settings, 
  Database,
  Server,
  Activity,
  AlertCircle,
  CheckCircle,
  Upload,
  Download
} from 'lucide-react';

const AdminSystem = () => {
  const [services, setServices] = useState({
    backend: 'unknown',
    frontend: 'unknown',
    database: 'unknown',
    nginx: 'unknown'
  });
  const [loading, setLoading] = useState(false);
  const [logs, setLogs] = useState([]);
  const [systemInfo, setSystemInfo] = useState(null);

  const checkServiceStatus = async () => {
    setLoading(true);
    try {
      const backendResponse = await fetch('/api/health');
      const backendStatus = backendResponse.ok ? 'running' : 'stopped';
      
      setServices(prev => ({ ...prev, backend: backendStatus }));
      
      // Получаем системную информацию с токеном авторизации
      if (backendResponse.ok) {
        const token = localStorage.getItem('admin_token');
        const systemResponse = await fetch('/api/admin/system/info', {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        if (systemResponse.ok) {
          const systemData = await systemResponse.json();
          setSystemInfo(systemData);
        }
      }
    } catch (error) {
      console.error('Error checking services:', error);
      setServices(prev => ({ ...prev, backend: 'error' }));
    } finally {
      setLoading(false);
    }
  };

  const restartService = async (serviceName) => {
    setLoading(true);
    try {
      const response = await fetch(`/api/admin/system/restart/${serviceName}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('admin_token')}`
        }
      });
      
      if (response.ok) {
        const result = await response.json();
        setLogs(prev => [...prev, {
          timestamp: new Date().toLocaleString(),
          message: `Сервис ${serviceName} перезапущен успешно`,
          type: 'success'
        }]);
        
        setTimeout(checkServiceStatus, 3000);
      } else {
        throw new Error(`Ошибка перезапуска ${serviceName}`);
      }
    } catch (error) {
      setLogs(prev => [...prev, {
        timestamp: new Date().toLocaleString(),
        message: `Ошибка перезапуска ${serviceName}: ${error.message}`,
        type: 'error'
      }]);
    } finally {
      setLoading(false);
    }
  };

  const clearCache = async () => {
    setLoading(true);
    try {
      const response = await fetch('/api/admin/system/cache/clear', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('admin_token')}`
        }
      });
      
      if (response.ok) {
        setLogs(prev => [...prev, {
          timestamp: new Date().toLocaleString(),
          message: 'Кеш очищен успешно',
          type: 'success'
        }]);
      }
    } catch (error) {
      setLogs(prev => [...prev, {
        timestamp: new Date().toLocaleString(),
        message: `Ошибка очистки кеша: ${error.message}`,
        type: 'error'
      }]);
    } finally {
      setLoading(false);
    }
  };

  const updateContent = async () => {
    setLoading(true);
    try {
      const response = await fetch('/api/admin/system/update', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('admin_token')}`
        }
      });
      
      if (response.ok) {
        setLogs(prev => [...prev, {
          timestamp: new Date().toLocaleString(),
          message: 'Контент обновлен успешно',
          type: 'success'
        }]);
      }
    } catch (error) {
      setLogs(prev => [...prev, {
        timestamp: new Date().toLocaleString(),
        message: `Ошибка обновления: ${error.message}`,
        type: 'error'
      }]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    checkServiceStatus();
    
    const interval = setInterval(checkServiceStatus, 30000);
    return () => clearInterval(interval);
  }, []);

  const getStatusColor = (status) => {
    switch (status) {
      case 'running': return 'bg-green-600';
      case 'stopped': return 'bg-red-600';
      case 'error': return 'bg-yellow-600';
      default: return 'bg-gray-600';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'running': return <CheckCircle className="h-4 w-4" />;
      case 'stopped': return <AlertCircle className="h-4 w-4" />;
      case 'error': return <AlertCircle className="h-4 w-4" />;
      default: return <Activity className="h-4 w-4" />;
    }
  };

  return (
    <div className="space-y-6 bg-gray-900 min-h-screen p-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-white">Управление системой</h1>
          <p className="text-gray-400 mt-1">Мониторинг и управление сервисами lngaua.ru</p>
        </div>
        <Button 
          onClick={checkServiceStatus} 
          disabled={loading}
          variant="outline"
          className="border-gray-600 text-gray-300 hover:bg-gray-700"
        >
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Обновить статус
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {Object.entries(services).map(([service, status]) => (
          <Card key={service} className="bg-gray-800 border-gray-700">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-gray-300 capitalize">{service}</CardTitle>
              {getStatusIcon(status)}
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <Badge className={`${getStatusColor(status)} text-white capitalize`}>
                  {status}
                </Badge>
                <Button
                  size="sm"
                  onClick={() => restartService(service)}
                  disabled={loading}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  <Power className="h-3 w-3 mr-1" />
                  Restart
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {systemInfo && (
        <Card className="bg-gray-800 border-gray-700">
          <CardHeader>
            <CardTitle className="text-white flex items-center">
              <Server className="h-5 w-5 mr-2" />
              Информация о системе
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <p className="text-gray-400 text-sm">CPU Usage</p>
                <p className="text-white font-semibold">{systemInfo.cpu_usage || 'N/A'}%</p>
              </div>
              <div>
                <p className="text-gray-400 text-sm">Memory Usage</p>
                <p className="text-white font-semibold">{systemInfo.memory_usage || 'N/A'}%</p>
              </div>
              <div>
                <p className="text-gray-400 text-sm">Disk Usage</p>
                <p className="text-white font-semibold">{systemInfo.disk_usage || 'N/A'}%</p>
              </div>
              <div>
                <p className="text-gray-400 text-sm">Uptime</p>
                <p className="text-white font-semibold">{systemInfo.uptime || 'N/A'}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Button
          onClick={clearCache}
          disabled={loading}
          className="bg-yellow-600 hover:bg-yellow-700 h-20 flex-col"
        >
          <RefreshCw className="h-6 w-6 mb-2" />
          Очистить кеш
        </Button>

        <Button
          onClick={updateContent}
          disabled={loading}
          className="bg-green-600 hover:bg-green-700 h-20 flex-col"
        >
          <Upload className="h-6 w-6 mb-2" />
          Обновить контент
        </Button>

        <Button
          onClick={() => restartService('all')}
          disabled={loading}
          className="bg-red-600 hover:bg-red-700 h-20 flex-col"
        >
          <Power className="h-6 w-6 mb-2" />
          Перезапустить все
        </Button>

        <Button
          onClick={() => window.open('/api/admin/system/backup', '_blank')}
          disabled={loading}
          className="bg-purple-600 hover:bg-purple-700 h-20 flex-col"
        >
          <Download className="h-6 w-6 mb-2" />
          Создать backup
        </Button>
      </div>

      <Card className="bg-gray-800 border-gray-700">
        <CardHeader>
          <CardTitle className="text-white flex items-center">
            <Activity className="h-5 w-5 mr-2" />
            Логи активности
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {logs.length === 0 ? (
              <p className="text-gray-400">Нет записей в логах</p>
            ) : (
              logs.slice(-10).reverse().map((log, index) => (
                <div key={index} className={`p-3 rounded ${
                  log.type === 'success' ? 'bg-green-900 text-green-100' : 'bg-red-900 text-red-100'
                }`}>
                  <div className="flex justify-between items-start">
                    <p className="text-sm">{log.message}</p>
                    <span className="text-xs opacity-75">{log.timestamp}</span>
                  </div>
                </div>
              ))
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminSystem;
